// Single source of truth for the 28 supported legal jurisdictions.
// Used by the Legal form dropdown AND by the backend prompt builder
// (mirrored in Backend/src/services/jurisdiction-profiles.js).

export const jurisdictionProfiles = [
  {
    code: 'IN',
    name: 'India',
    legalSystem: 'Common Law',
    criminalCode: 'Indian Penal Code (IPC), 1860',
    proceduralCode: 'Code of Criminal Procedure (CrPC), 1973',
    evidenceLaw: 'Indian Evidence Act, 1872',
    courts: ['Magistrate Court', 'Sessions Court', 'High Court', 'Supreme Court'],
    prosecutionAuthority: 'Public Prosecutor',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['FIR', 'Charge Sheet', 'Cognizable Offence', 'Bail', 'Summons'],
  },
  {
    code: 'US',
    name: 'United States',
    legalSystem: 'Common Law',
    criminalCode: 'US Code (Title 18) and State Penal Codes',
    proceduralCode: 'Federal Rules of Criminal Procedure',
    evidenceLaw: 'Federal Rules of Evidence',
    courts: ['District Court', 'Circuit Court of Appeals', 'Supreme Court'],
    prosecutionAuthority: 'District Attorney / US Attorney',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Miranda Rights', 'Grand Jury', 'Arraignment', 'Indictment'],
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    legalSystem: 'Common Law',
    criminalCode: 'Theft Act 1968 / Criminal Law Act',
    proceduralCode: 'Police and Criminal Evidence Act 1984 (PACE)',
    evidenceLaw: 'Criminal Evidence (Witness Anonymity) Act 2008',
    courts: ["Magistrates' Court", 'Crown Court', 'Court of Appeal', 'Supreme Court'],
    prosecutionAuthority: 'Crown Prosecution Service (CPS)',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Indictable Offence', 'Charge', 'Custody Record', 'Disclosure'],
  },
  {
    code: 'CA',
    name: 'Canada',
    legalSystem: 'Common Law',
    criminalCode: 'Criminal Code (RSC 1985, c. C-46)',
    proceduralCode: 'Criminal Code procedure (Part XX, XXI)',
    evidenceLaw: 'Canada Evidence Act',
    courts: ['Provincial Court', 'Superior Court', 'Court of Appeal', 'Supreme Court of Canada'],
    prosecutionAuthority: 'Crown Prosecutor',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Indictable Offence', 'Summary Offence', 'Disclosure', 'Preliminary Inquiry'],
  },
  {
    code: 'AU',
    name: 'Australia',
    legalSystem: 'Common Law',
    criminalCode: 'Criminal Code Act 1995 (Cth) / State Crimes Acts',
    proceduralCode: 'Criminal Procedure Acts (state/territory)',
    evidenceLaw: 'Evidence Act 1995 (Cth)',
    courts: ["Magistrates' Court", 'District/County Court', 'Supreme Court', 'High Court of Australia'],
    prosecutionAuthority: 'Director of Public Prosecutions',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Committal Hearing', 'Indictable Offence', 'Brief of Evidence'],
  },
  {
    code: 'DE',
    name: 'Germany',
    legalSystem: 'Civil Law',
    criminalCode: 'Strafgesetzbuch (StGB)',
    proceduralCode: 'Strafprozessordnung (StPO)',
    evidenceLaw: 'StPO (evidence provisions)',
    courts: ['Amtsgericht', 'Landgericht', 'Oberlandesgericht', 'Bundesgerichtshof'],
    prosecutionAuthority: 'Staatsanwaltschaft',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Staatsanwaltschaft', 'Amtsgericht', 'Haftbefehl', 'Ermittlungsverfahren'],
  },
  {
    code: 'FR',
    name: 'France',
    legalSystem: 'Civil Law',
    criminalCode: 'Code Pénal',
    proceduralCode: 'Code de Procédure Pénale',
    evidenceLaw: 'Code de Procédure Pénale (evidence rules)',
    courts: ['Tribunal Judiciaire', "Cour d'Appel", 'Cour de Cassation'],
    prosecutionAuthority: 'Procureur de la République',
    standardOfProof: 'Intime conviction (inner conviction)',
    proceduralTerms: ['Garde à vue', 'Procès-verbal', 'Juge d’instruction', 'Tribunal Correctionnel'],
  },
  {
    code: 'ES',
    name: 'Spain',
    legalSystem: 'Civil Law',
    criminalCode: 'Código Penal',
    proceduralCode: 'Ley de Enjuiciamiento Criminal',
    evidenceLaw: 'Ley de Enjuiciamiento Criminal (evidence rules)',
    courts: ['Juzgado de Instrucción', 'Audiencia Provincial', 'Tribunal Supremo'],
    prosecutionAuthority: 'Fiscalía',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Denuncia', 'Juzgado de Instrucción', 'Delito Flagrante'],
  },
  {
    code: 'IT',
    name: 'Italy',
    legalSystem: 'Civil Law',
    criminalCode: 'Codice Penale',
    proceduralCode: 'Codice di Procedura Penale',
    evidenceLaw: 'Codice di Procedura Penale (evidence rules)',
    courts: ['Tribunale', "Corte d'Appello", 'Corte di Cassazione'],
    prosecutionAuthority: 'Pubblico Ministero',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Pubblico Ministero', 'Indagini Preliminari', 'Giudice per le Indagini Preliminari'],
  },
  {
    code: 'NL',
    name: 'Netherlands',
    legalSystem: 'Civil Law',
    criminalCode: 'Wetboek van Strafrecht',
    proceduralCode: 'Wetboek van Strafvordering',
    evidenceLaw: 'Wetboek van Strafvordering (evidence rules)',
    courts: ['Rechtbank', 'Gerechtshof', 'Hoge Raad'],
    prosecutionAuthority: 'Openbaar Ministerie',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Openbaar Ministerie', 'Rechter-Commissaris', 'Dagvaarding'],
  },
  {
    code: 'SE',
    name: 'Sweden',
    legalSystem: 'Civil Law',
    criminalCode: 'Brottsbalken',
    proceduralCode: 'Rättegångsbalken',
    evidenceLaw: 'Rättegångsbalken (evidence rules)',
    courts: ['Tingsrätt', 'Hovrätt', 'Högsta domstolen'],
    prosecutionAuthority: 'Åklagarmyndigheten',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Förundersökning', 'Åtal', 'Häktning'],
  },
  {
    code: 'NO',
    name: 'Norway',
    legalSystem: 'Civil Law',
    criminalCode: 'Straffeloven',
    proceduralCode: 'Straffeprosessloven',
    evidenceLaw: 'Straffeprosessloven (evidence rules)',
    courts: ['Tingrett', 'Lagmannsrett', 'Høyesterett'],
    prosecutionAuthority: 'Påtalemyndigheten',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Etterforskning', 'Tiltale', 'Varetekt'],
  },
  {
    code: 'DK',
    name: 'Denmark',
    legalSystem: 'Civil Law',
    criminalCode: 'Straffeloven',
    proceduralCode: 'Retsplejeloven',
    evidenceLaw: 'Retsplejeloven (evidence rules)',
    courts: ['Byret', 'Landsret', 'Højesteret'],
    prosecutionAuthority: 'Anklagemyndigheden',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Efterforskning', 'Tiltale', 'Varetægtsfængsling'],
  },
  {
    code: 'FI',
    name: 'Finland',
    legalSystem: 'Civil Law',
    criminalCode: 'Rikoslaki',
    proceduralCode: 'Oikeudenkäymiskaari',
    evidenceLaw: 'Oikeudenkäymiskaari (evidence rules)',
    courts: ['Käräjäoikeus', 'Hovioikeus', 'Korkein oikeus'],
    prosecutionAuthority: 'Syyttäjälaitos',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Esitutkinta', 'Syyte', 'Vangitseminen'],
  },
  {
    code: 'CH',
    name: 'Switzerland',
    legalSystem: 'Civil Law',
    criminalCode: 'Strafgesetzbuch (StGB) / Code Pénal (CP)',
    proceduralCode: 'Strafprozessordnung (StPO) / Code de Procédure Pénale (CPP)',
    evidenceLaw: 'StPO/CPP (evidence rules)',
    courts: ['Bezirksgericht', 'Kantonsgericht', 'Bundesgericht'],
    prosecutionAuthority: 'Staatsanwaltschaft',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Staatsanwaltschaft', 'Untersuchungshaft', 'Strafbefehl'],
  },
  {
    code: 'BE',
    name: 'Belgium',
    legalSystem: 'Civil Law',
    criminalCode: 'Strafwetboek / Code Pénal',
    proceduralCode: 'Wetboek van Strafvordering / Code d’Instruction Criminelle',
    evidenceLaw: 'Code d’Instruction Criminelle (evidence rules)',
    courts: ['Rechtbank', 'Hof van Beroep', 'Hof van Cassatie'],
    prosecutionAuthority: 'Openbaar Ministerie / Ministère Public',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Openbaar Ministerie', 'Onderzoeksrechter', 'Aanhouding'],
  },
  {
    code: 'AT',
    name: 'Austria',
    legalSystem: 'Civil Law',
    criminalCode: 'Strafgesetzbuch (StGB)',
    proceduralCode: 'Strafprozessordnung (StPO)',
    evidenceLaw: 'StPO (evidence rules)',
    courts: ['Bezirksgericht', 'Landesgericht', 'Oberster Gerichtshof'],
    prosecutionAuthority: 'Staatsanwaltschaft',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Staatsanwaltschaft', 'Untersuchungshaft', 'Anklageschrift'],
  },
  {
    code: 'PT',
    name: 'Portugal',
    legalSystem: 'Civil Law',
    criminalCode: 'Código Penal',
    proceduralCode: 'Código de Processo Penal',
    evidenceLaw: 'Código de Processo Penal (evidence rules)',
    courts: ['Tribunal Judicial', 'Tribunal da Relação', 'Supremo Tribunal de Justiça'],
    prosecutionAuthority: 'Ministério Público',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Ministério Público', 'Inquérito', 'Prisão Preventiva'],
  },
  {
    code: 'GR',
    name: 'Greece',
    legalSystem: 'Civil Law',
    criminalCode: 'Poinikos Kodikas (Penal Code)',
    proceduralCode: 'Kodikas Poinikis Dikonomias (Code of Criminal Procedure)',
    evidenceLaw: 'Code of Criminal Procedure (evidence rules)',
    courts: ['Plimmeleiodikeio', 'Efeteio', 'Areios Pagos'],
    prosecutionAuthority: 'Eisangeleas (Public Prosecutor)',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Eisangeleas', 'Proanakrisi', 'Prosorini Kratisi'],
  },
  {
    code: 'IE',
    name: 'Ireland',
    legalSystem: 'Common Law',
    criminalCode: 'Criminal Law Act 1997 / Criminal Justice Acts',
    proceduralCode: 'Criminal Procedure Act 2010',
    evidenceLaw: 'Criminal Evidence Act 1992',
    courts: ['District Court', 'Circuit Court', 'High Court', 'Supreme Court'],
    prosecutionAuthority: 'Director of Public Prosecutions',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Charge Sheet', 'Book of Evidence', 'Indictable Offence'],
  },
  {
    code: 'PL',
    name: 'Poland',
    legalSystem: 'Civil Law',
    criminalCode: 'Kodeks Karny',
    proceduralCode: 'Kodeks Postępowania Karnego',
    evidenceLaw: 'Kodeks Postępowania Karnego (evidence rules)',
    courts: ['Sąd Rejonowy', 'Sąd Okręgowy', 'Sąd Najwyższy'],
    prosecutionAuthority: 'Prokuratura',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Prokuratura', 'Postępowanie Przygotowawcze', 'Tymczasowe Aresztowanie'],
  },
  {
    code: 'CZ',
    name: 'Czech Republic',
    legalSystem: 'Civil Law',
    criminalCode: 'Trestní zákoník',
    proceduralCode: 'Trestní řád',
    evidenceLaw: 'Trestní řád (evidence rules)',
    courts: ['Okresní soud', 'Krajský soud', 'Nejvyšší soud'],
    prosecutionAuthority: 'Státní zastupitelství',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Státní zastupitelství', 'Přípravné řízení', 'Vazba'],
  },
  {
    code: 'HU',
    name: 'Hungary',
    legalSystem: 'Civil Law',
    criminalCode: 'Büntető Törvénykönyv',
    proceduralCode: 'Büntetőeljárási Törvény (Be.)',
    evidenceLaw: 'Be. (evidence rules)',
    courts: ['Járásbíróság', 'Törvényszék', 'Kúria'],
    prosecutionAuthority: 'Ügyészség',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Ügyészség', 'Nyomozás', 'Előzetes Letartóztatás'],
  },
  {
    code: 'RO',
    name: 'Romania',
    legalSystem: 'Civil Law',
    criminalCode: 'Codul Penal',
    proceduralCode: 'Codul de Procedură Penală',
    evidenceLaw: 'Codul de Procedură Penală (evidence rules)',
    courts: ['Judecătorie', 'Tribunal', 'Înalta Curte de Casație și Justiție'],
    prosecutionAuthority: 'Parchet',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Parchet', 'Urmărire Penală', 'Arest Preventiv'],
  },
  {
    code: 'ZA',
    name: 'South Africa',
    legalSystem: 'Mixed (Common Law + Customary)',
    criminalCode: 'Criminal Procedure Act 51 of 1977',
    proceduralCode: 'Criminal Procedure Act 51 of 1977',
    evidenceLaw: 'Law of Evidence Amendment Act 45 of 1988',
    courts: ["Magistrates' Court", 'High Court', 'Supreme Court of Appeal', 'Constitutional Court'],
    prosecutionAuthority: 'National Prosecuting Authority',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Draft Charge Sheet', 'Bail Application', 'Prima Facie Case'],
  },
  {
    code: 'SG',
    name: 'Singapore',
    legalSystem: 'Common Law',
    criminalCode: 'Penal Code 1871',
    proceduralCode: 'Criminal Procedure Code 2010',
    evidenceLaw: 'Evidence Act 1893',
    courts: ['State Courts', 'High Court', 'Court of Appeal'],
    prosecutionAuthority: "Attorney-General's Chambers",
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Mention', 'Committal Hearing', 'Charge Sheet'],
  },
  {
    code: 'MY',
    name: 'Malaysia',
    legalSystem: 'Mixed (Common Law + Syariah)',
    criminalCode: 'Penal Code (Act 574)',
    proceduralCode: 'Criminal Procedure Code (Act 593)',
    evidenceLaw: 'Evidence Act 1950',
    courts: ["Magistrates' Court", 'Sessions Court', 'High Court', 'Federal Court'],
    prosecutionAuthority: 'Public Prosecutor',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Charge Sheet', 'Mention', 'Bail Application'],
  },
  {
    code: 'NZ',
    name: 'New Zealand',
    legalSystem: 'Common Law',
    criminalCode: 'Crimes Act 1961',
    proceduralCode: 'Criminal Procedure Act 2011',
    evidenceLaw: 'Evidence Act 2006',
    courts: ['District Court', 'High Court', 'Court of Appeal', 'Supreme Court'],
    prosecutionAuthority: 'Crown Law Office',
    standardOfProof: 'Beyond reasonable doubt',
    proceduralTerms: ['Charge Notice', 'Committal', 'Indictable Offence'],
  },
];

export function getJurisdiction(name) {
  return jurisdictionProfiles.find((j) => j.name === name);
}

export function getJurisdictionByCode(code) {
  return jurisdictionProfiles.find((j) => j.code === code);
}
